import React, { useState, useEffect } from 'react';
import s from './classic/styles.module.scss';
import {
  sanitizeInput,
  validateEmail,
  validatePhone,
  validateName,
  validateText,
  validateNumber,
  validateUrl,
  validateDate,
  validatePassword,
  validatePin,
  validateAddress
} from '../../helpers/inputValidation';

const SecureInput = ({
  label,
  type = 'text',
  name,
  placeholder,
  value,
  onChange,
  onFocus,
  onBlur,
  iconLeft,
  iconRight,
  onLeftIconClick,
  onRightIconClick,
  disabled = false,
  required = false,
  validationType = null, // 'email', 'phone', 'name', 'text', 'number', 'url', 'date', 'password', 'pin', 'address'
  validationParams = {}, // Additional parameters for validation
  showError = true,
  className = '',
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Validation function mapping
  const getValidationFunction = () => {
    switch (validationType) {
      case 'email':
        return validateEmail;
      case 'phone':
        return validatePhone;
      case 'name':
        return (value) => validateName(value, label || 'Name');
      case 'text':
        return (value) => validateText(value, label || 'Text', validationParams.minLength, validationParams.maxLength);
      case 'number':
        return (value) => validateNumber(value, label || 'Number', validationParams.min, validationParams.max);
      case 'url':
        return (value) => validateUrl(value, label || 'URL');
      case 'date':
        return (value) => validateDate(value, label || 'Date');
      case 'password':
        return validatePassword;
      case 'pin':
        return validatePin;
      case 'address':
        return validateAddress;
      default:
        return null;
    }
  };

  // Handle input change with validation
  const handleChange = (e) => {
    const rawValue = e.target.value;
    const validationFn = getValidationFunction();
    
    // Always sanitize the input
    const sanitizedValue = sanitizeInput(rawValue);
    
    if (validationFn) {
      const validation = validationFn(sanitizedValue);
      
      if (validation.isValid) {
        setError('');
        setInputValue(validation.value);
        onChange && onChange({ ...e, target: { ...e.target, value: validation.value } });
      } else {
        setError(validation.message);
        setInputValue(sanitizedValue);
        onChange && onChange(e);
      }
    } else {
      // No validation, just sanitize
      setError('');
      setInputValue(sanitizedValue);
      onChange && onChange({ ...e, target: { ...e.target, value: sanitizedValue } });
    }
  };

  // Handle focus
  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus && onFocus(e);
  };

  // Handle blur
  const handleBlur = (e) => {
    setIsFocused(false);
    
    // Validate on blur if validation is enabled
    if (validationType && inputValue) {
      const validationFn = getValidationFunction();
      if (validationFn) {
        const validation = validationFn(inputValue);
        if (!validation.isValid) {
          setError(validation.message);
        }
      }
    }
    
    onBlur && onBlur(e);
  };

  // Handle key down
  const handleKeyDown = (e) => {
    if (e.keyCode === 38 || e.keyCode === 40) {
      e.preventDefault();
    }
    props.onKeyDown && props.onKeyDown(e);
  };

  return (
    <div className={`${s.field} ${className}`}>
      {!!label && (
        <label className={s.label}>
          {label}
          {required && <span style={{ color: 'red' }}> *</span>}
        </label>
      )}
      <div className={s.fieldData}>
        {iconLeft && (
          <span
            className={(isFocused ? 'material-icons ' : 'material-icons-outlined ') + s.iconLeft}
            onClick={onLeftIconClick ? onLeftIconClick : () => {}}
            style={{ cursor: onLeftIconClick && 'pointer' }}
          >
            {iconLeft}
          </span>
        )}
        <input
          name={name}
          className={`${s.input} ${error ? s.error : ''}`}
          type={type}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoComplete='off'
          disabled={disabled}
          required={required}
          {...props}
        />
        {iconRight && (
          <div className={s.speech} onClick={!props.speechStatus ? onRightIconClick : () => {}}>
            {iconRight === 'search' && <div className={s.searchText}>search</div>}
            <span
              className={
                (isFocused ? 'material-icons ' : 'material-icons-outlined ') +
                s.iconRight +
                (iconRight === 'mic' ? ' ' + s.mic : '')
              }
              style={{
                marginLeft: iconRight === 'search' ? '0rem' : '0.5rem',
                paddingLeft: iconRight === 'search' && '0.25rem'
              }}
            >
              {iconRight}
            </span>
            {props.speechStatus && (
              <div className={s.circles}>
                <div className={s.circle1}></div>
                <div className={s.circle2}></div>
                <div className={s.circle3}></div>
              </div>
            )}
          </div>
        )}
      </div>
      {showError && error && (
        <div className={s.errorMessage} style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.25rem' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default SecureInput; 